import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import {
    Calendar,
    ArrowRight,
    MapPin,
    Award,
    BookOpen,
    MessageCircle,
    Globe,
    Instagram,
    Facebook,
    Brain,
    Shield,
    Star,
    Phone,
    Mail,
    GraduationCap,
    Video,
    Sparkles,
    Target,
} from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { fetchPublicPsychologyProfileData } from "./humanPsychologyApi";
import PublicPsychologySessions from "./PublicPsychologySessions";

interface SocialLinks {
    facebook: string | null;
    instagram: string | null;
    telegram: string | null;
    youtube: string | null;
    demoVideo: string | null;
    whatsapp: string | null;
}

interface LocationData {
    state?: string;
    city?: string;
    address?: string;
}

interface ConsultationClass {
    _id: string;
    classTitle?: string;
    title?: string;
    classDescription?: string;
    description?: string;
    classThumbnail?: string;
    thumbnail?: string;
    classCategory?: string;
    category?: string;
    classStartDate?: string;
    startDate?: string;
    classEndDate?: string;
    endDate?: string;
    classDuration?: string;
    duration?: string;
    classPrice?: {
        session?: string;
        monthly?: string;
    };
    prices?: {
        session?: string;
        monthly?: string;
    };
    purchaseStatus?: string;
    status?: string;
}

interface Instructor {
    _id: string;
    bio: string;
    certification: string;
    firstname: string;
    languages: string[];
    lastname: string;
    location: string;
    mentorId: string;
    profileIcon: string;
    Skills: string[];
    totalClass: string;
    experience: string;
    joinDate: string;
    socialLinks: SocialLinks;
    totalStudents: string;
    upcomingClasses: ConsultationClass[];
    profileQuote: string;
    profileBanner: string;
    useremail: string;
    phone: string;
    createAt: string;
    specializations?: string[];
}

// New interface for the incoming backend data
interface BackendInstructorRaw {
    _id: string;
    firstName?: string;
    lastName?: string;
    bio?: string;
    certification?: string;
    email?: string;
    languages?: string[];
    location?: LocationData | string;
    phone?: string;
    skill?: string[];
    socialLinks?: SocialLinks;
    specializations?: string[];
    totalEarnings?: number;
    totalStudents?: number;
    upcomingClasses?: ConsultationClass[];
    upcomingConsultations?: ConsultationClass[];
    profileIcon?: string;
    profileBanner?: string;
    profileQuote?: string;
    experience?: string;
}

interface BackendResponse {
    data: BackendInstructorRaw;
    success: boolean;
}

export default function HumanPsychologyProfile() {
    const [activeTab, setActiveTab] = useState<"sessions" | "about" | "blogs">("sessions");
    const [favorites, setFavorites] = useState<Set<string>>(new Set());
    const [isVerified, setVerified] = useState(false);
    const params = useParams();
    const navigate = useNavigate();
    const [showCertificate, setShowCertificate] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [showAllSkills, setShowAllSkills] = useState(false);
    const [showAllSidebarSkills, setShowAllSidebarSkills] = useState(false);

    const [instructor, setInstructor] = useState<Instructor>({
        _id: "",
        bio: "",
        certification: "",
        firstname: "",
        languages: [],
        lastname: "",
        location: "",
        mentorId: "",
        profileIcon: "",
        Skills: [],
        totalClass: "",
        experience: "",
        joinDate: "",
        socialLinks: {
            facebook: null,
            instagram: null,
            telegram: null,
            youtube: null,
            demoVideo: null,
            whatsapp: null,
        },
        totalStudents: "",
        upcomingClasses: [],
        profileQuote: "",
        profileBanner: "",
        useremail: "",
        phone: "",
        createAt: "",
        specializations: [],
    });

    // Helper to format location object into a readable string
    const formatLocation = (location: LocationData | string | undefined): string => {
        if (!location) return "Not specified";
        if (typeof location === "string") return location;
        const parts = [];
        if (location.address) parts.push(location.address);
        if (location.city) parts.push(location.city);
        if (location.state) parts.push(location.state);
        return parts.length > 0 ? parts.join(", ") : "Not specified";
    };

    const handleProfileData = async () => {
        if (params["id"]) {
            try {
                // Fetch the raw Axios response
                const response = await fetchPublicPsychologyProfileData(params["id"]?.toString());
                // Axios wraps the actual payload in the `data` property
                const res: BackendResponse = response?.data ?? response;
                if (res?.success && res?.data) {
                    const raw = res.data;

                    // Map upcomingConsultations to match the ConsultationClass interface
                    const mappedConsultations = (raw.upcomingConsultations || []).map((c: any) => ({
                        _id: c._id,
                        title: c.consultationTitle || c.title,
                        description: c.consultationDescription || c.description,
                        category: c.consultationType || c.category,
                        thumbnail: c.consultationThumbnail || c.thumbnail,
                        startDate: c.consultationStartDate || c.startDate,
                        endDate: c.consultationEndDate || c.endDate,
                        duration: c.consultationDuration || c.duration,
                        prices: { session: c.consultationPrice || c.prices?.session },
                        pricingType: c.pricingType,
                        status: c.status
                    }));

                    // Merge upcomingClasses and mapped consultations
                    const allUpcoming = [
                        ...(raw.upcomingClasses || []),
                        ...mappedConsultations,
                    ];

                    const defaultSocialLinks: SocialLinks = {
                        facebook: null,
                        instagram: null,
                        telegram: null,
                        youtube: null,
                        demoVideo: null,
                        whatsapp: null,
                    };

                    // Transform backend data to the Instructor interface
                    const profileData: Instructor = {
                        _id: raw._id || "",
                        bio: raw.bio || "Welcome to my profile! I'm a dedicated psychology specialist passionate about helping individuals achieve mental wellness and emotional balance.",
                        certification: raw.certification || "",
                        firstname: raw.firstName || "",
                        languages: raw.languages || [],
                        lastname: raw.lastName || "",
                        location: formatLocation(raw.location),
                        mentorId: "",
                        profileIcon: raw.profileIcon || "",
                        Skills: raw.skill || raw.specializations || [],
                        totalClass: String(allUpcoming.length || 0),
                        experience: raw.experience || "",
                        joinDate: "",
                        socialLinks: raw.socialLinks || defaultSocialLinks,
                        totalStudents: String(raw.totalStudents || 0),
                        upcomingClasses: allUpcoming,
                        profileQuote: raw.profileQuote || "",
                        profileBanner: raw.profileBanner || "",
                        useremail: raw.email || "",
                        phone: raw.phone || "",
                        createAt: "", // Not provided in new API
                        specializations: raw.specializations || raw.skill || [],
                    };

                    setInstructor(profileData);
                    setVerified(true);
                }
            } catch (error) {
                console.error("Error fetching profile:", error);
            }
        } else {
            navigate("/", { replace: true });
        }
    };

    useEffect(() => {
        handleProfileData();
    }, [params.id]);

    const getAbsoluteImageUrl = (imageUrl: string | undefined): string => {
        if (!imageUrl) return "";
        if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) return imageUrl;
        const baseUrl = window.location.origin;
        return `${baseUrl}${imageUrl.startsWith("/") ? "" : "/"}${imageUrl}`;
    };

    const profileImageUrl = instructor.profileIcon
        ? getAbsoluteImageUrl(instructor.profileIcon)
        : `https://ui-avatars.com/api/?name=${encodeURIComponent(
            instructor.firstname + " " + instructor.lastname
        )}&background=7c3aed&color=fff&size=200&bold=true`;

    return (
        <div className="bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50 min-h-screen relative overflow-hidden">
            <Helmet>
                <title>{`${instructor.firstname} ${instructor.lastname} - Psychology Specialist | MetaFit`}</title>
                <meta
                    name="description"
                    content={
                        instructor.bio ||
                        `Experienced psychology specialist with ${instructor.totalClass} sessions and ${instructor.totalStudents} clients helped.`
                    }
                />
                <meta property="og:type" content="profile" />
                <meta property="og:url" content={window.location.href} />
                <meta property="og:title" content={`${instructor.firstname} ${instructor.lastname} - Psychology Specialist | MetaFit`} />
                <meta
                    property="og:description"
                    content={
                        instructor.bio ||
                        `Connect with ${instructor.firstname} ${instructor.lastname}, a certified psychology specialist.`
                    }
                />
                <meta property="og:image" content={profileImageUrl} />
                <meta property="og:image:secure_url" content={profileImageUrl} />
                <meta property="og:image:type" content="image/jpeg" />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:image:alt" content={`${instructor.firstname} ${instructor.lastname} - Professional Psychology Specialist`} />
                <meta property="og:site_name" content="MetaFit - Mental Wellness Platform" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={`${instructor.firstname} ${instructor.lastname} - Psychology Specialist`} />
                <meta name="twitter:description" content={instructor.bio || `Professional psychology specialist`} />
                <meta name="twitter:image" content={profileImageUrl} />
                <meta name="twitter:image:alt" content={`${instructor.firstname} ${instructor.lastname} Profile`} />
                <link rel="canonical" href={window.location.href} />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Person",
                        name: `${instructor.firstname} ${instructor.lastname}`,
                        jobTitle: "Psychology Specialist",
                        description: instructor.bio,
                        image: profileImageUrl,
                        url: window.location.href,
                        address: { "@type": "PostalAddress", addressLocality: instructor.location },
                        knowsAbout: instructor.Skills,
                        alumniOf: instructor.certification,
                        sameAs: [
                            instructor.socialLinks?.instagram,
                            instructor.socialLinks?.facebook,
                            instructor.socialLinks?.youtube,
                            instructor.socialLinks?.telegram,
                        ].filter(Boolean),
                    })}
                </script>
            </Helmet>

            {/* Background decorative blobs */}
            <div className="absolute inset-0 z-0 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-br from-violet-50/40 via-purple-50/30 to-indigo-50/40" />
                <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-violet-200/20 rounded-full blur-3xl animate-pulse" />
                <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-200/20 rounded-full blur-3xl animate-pulse animation-delay-2000" />
                <div className="absolute top-1/2 left-1/2 w-72 h-72 bg-indigo-200/20 rounded-full blur-3xl animate-pulse animation-delay-4000" />
            </div>

            <div className="relative z-10 mt-14 max-w-6xl mx-auto px-4 py-8">

                {/* ── PROFILE HEADER CARD ── */}
                <div className="relative bg-white/80 backdrop-blur-xl rounded-3xl shadow-xl border border-purple-200 p-6 mb-6">
                    {/* Banner */}
                    <div
                        className="absolute top-0 left-0 w-full h-40 bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 rounded-t-3xl"
                        style={instructor.profileBanner ? {
                            backgroundImage: `url(${getAbsoluteImageUrl(instructor.profileBanner)})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center'
                        } : {}}
                    >
                        {/* Decorative overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-t-3xl" />
                        <div className="absolute inset-0 opacity-20"
                            style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
                    </div>

                    <div className="relative flex flex-col lg:flex-row gap-6 items-start">
                        {/* ── LEFT COLUMN ── */}
                        <div className="flex flex-col items-center lg:items-start w-full lg:w-72 flex-shrink-0">
                            {/* Avatar */}
                            <div className="relative -mt-24 mb-4">
                                {instructor.profileIcon && !imageError ? (
                                    <img
                                        src={profileImageUrl}
                                        alt={`${instructor.firstname} ${instructor.lastname}`}
                                        className="w-32 h-32 rounded-2xl object-cover border-4 border-white shadow-xl"
                                        onError={() => setImageError(true)}
                                    />
                                ) : (
                                    <div className="w-32 h-32 rounded-2xl bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-600 flex items-center justify-center border-4 border-white shadow-xl">
                                        <span className="text-4xl font-bold text-white">
                                            {instructor.firstname?.charAt(0)}{instructor.lastname?.charAt(0)}
                                        </span>
                                    </div>
                                )}
                                <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-gradient-to-r from-violet-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                                    <Shield className="w-4 h-4 text-white" />
                                </div>
                            </div>

                            {/* Details card */}
                            <div className="w-full mt-8 bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl p-4 border border-purple-100 space-y-2">
                                {/* Experience */}
                                <div className="flex items-center gap-2.5">
                                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                        <GraduationCap className="w-4 h-4 text-purple-600" />
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Experience</p>
                                        <p className="text-sm text-gray-700">{instructor.experience || "Certified Specialist"}</p>
                                    </div>
                                </div>

                                {/* Languages */}
                                <div className="flex items-center gap-2.5">
                                    <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                        <Globe className="w-4 h-4 text-indigo-600" />
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Languages</p>
                                        <p className="text-sm text-gray-700">{instructor.languages?.join(", ") || "English"}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Areas of Expertise */}
                            {instructor.Skills && instructor.Skills.length > 0 && (
                                <div className="w-full mt-4 bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl p-4 border border-purple-100 space-y-3">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <Target className="w-4 h-4 text-violet-600" />
                                        </div>
                                        <div>
                                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Areas of Expertise</p>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5 pt-1">
                                        {(showAllSidebarSkills ? instructor.Skills : instructor.Skills.slice(0, 4)).map((spec, index) => (
                                            <span
                                                key={index}
                                                className="bg-white text-violet-700 px-2.5 py-1 rounded-lg text-xs font-semibold border border-purple-100 shadow-sm"
                                            >
                                                {spec}
                                            </span>
                                        ))}
                                        {instructor.Skills.length > 4 && (
                                            <button
                                                onClick={() => setShowAllSidebarSkills(!showAllSidebarSkills)}
                                                className="bg-violet-100 text-violet-800 hover:bg-violet-200 px-2.5 py-1 rounded-lg text-xs font-bold border border-violet-200 shadow-sm transition-all duration-200"
                                            >
                                                {showAllSidebarSkills ? "Show Less" : `+ ${instructor.Skills.length - 4} More`}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Social links */}
                            {(instructor.socialLinks?.instagram ||
                                instructor.socialLinks?.facebook ||
                                instructor.socialLinks?.telegram ||
                                instructor.socialLinks?.youtube ||
                                instructor.socialLinks?.demoVideo) && (
                                    <div className="w-full mt-4">
                                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-2">Connect</p>
                                        <div className="flex flex-wrap gap-2">
                                            {instructor.socialLinks?.instagram && (
                                                <a href={instructor.socialLinks.instagram} target="_blank" rel="noopener noreferrer"
                                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-lg text-xs font-medium hover:shadow-md transition-all hover:scale-105">
                                                    <Instagram className="w-3.5 h-3.5" /> Instagram
                                                </a>
                                            )}
                                            {instructor.socialLinks?.facebook && (
                                                <a href={instructor.socialLinks.facebook} target="_blank" rel="noopener noreferrer"
                                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:shadow-md transition-all hover:scale-105">
                                                    <Facebook className="w-3.5 h-3.5" /> Facebook
                                                </a>
                                            )}
                                            {instructor.socialLinks?.youtube && (
                                                <a href={instructor.socialLinks.youtube} target="_blank" rel="noopener noreferrer"
                                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-medium hover:shadow-md transition-all hover:scale-105">
                                                    <Video className="w-3.5 h-3.5" /> YouTube
                                                </a>
                                            )}
                                            {instructor.socialLinks?.whatsapp && (
                                                <a href={`https://wa.me/${instructor.socialLinks.whatsapp}`} target="_blank" rel="noopener noreferrer"
                                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:shadow-md transition-all hover:scale-105">
                                                    <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                                                </a>
                                            )}
                                            {instructor.socialLinks?.demoVideo && (
                                                <a href={instructor.socialLinks.demoVideo} target="_blank" rel="noopener noreferrer"
                                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 text-white rounded-lg text-xs font-medium hover:shadow-md transition-all hover:scale-105">
                                                    <Video className="w-3.5 h-3.5" /> Demo
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                )}
                        </div>

                        {/* ── RIGHT COLUMN ── */}
                        <div className="flex-1 space-y-4 md:mt-[18vh]">
                            {/* Name + badge */}
                            <div className="flex flex-col gap-3">
                                <div className="flex flex-col lg:flex-row lg:items-center gap-2 justify-center lg:justify-start mb-2">
                                    <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                                        {instructor.firstname} {instructor.lastname}
                                    </h1>
                                    {isVerified && (
                                        <div className="flex items-center gap-1 bg-gradient-to-r from-violet-600 to-purple-600 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-sm w-fit mx-auto lg:mx-0">
                                            <Shield className="w-3.5 h-3.5" /> Verified Specialist
                                        </div>
                                    )}
                                </div>

                                {/* Specialty */}
                                <p className="text-violet-600 font-medium text-sm mb-3">
                                    <Brain className="w-4 h-4 inline mr-1" />
                                    Psychology & Mental Wellness Specialist
                                </p>

                                {/* Stats */}
                                <div className="flex gap-6 justify-center lg:justify-start mb-4">
                                    <div className="text-center lg:text-left">
                                        <div className="text-xl font-bold text-gray-800">{instructor.totalClass || 0}</div>
                                        <div className="text-xs text-gray-400">Sessions</div>
                                    </div>
                                    <div className="w-px bg-gray-200" />
                                    <div className="text-center lg:text-left">
                                        <div className="text-xl font-bold text-gray-800">{instructor.totalStudents || 0}</div>
                                        <div className="text-xs text-gray-400">Happy Customer</div>
                                    </div>
                                    <div className="w-px bg-gray-200" />
                                    <div className="text-center lg:text-left">
                                        <div className="text-xl font-bold text-gray-800">
                                            <Star className="w-5 h-5 text-amber-500 fill-amber-500 inline mr-1 -mt-1" />
                                            4.8
                                        </div>
                                        <div className="text-xs text-gray-400">Rating</div>
                                    </div>
                                </div>
                            </div>

                            {/* Profile quote */}
                            {instructor.profileQuote && (
                                <div className="bg-gradient-to-r from-violet-50 to-purple-50 border-l-4 border-violet-500 px-4 py-3 rounded-r-xl">
                                    <p
                                        className="text-gray-600 text-sm italic leading-relaxed text-center lg:text-left"
                                        dangerouslySetInnerHTML={{ __html: instructor.profileQuote.replace(/ style="[^"]*"/g, '') }}
                                    />
                                </div>
                            )}

                            {/* Bio */}
                            <div className="bg-white rounded-2xl p-6 shadow-sm border border-purple-100">
                                <h3>About Me</h3>
                                <p className="leading-8 text-gray-600">
                                    {instructor.bio || ""}
                                </p>
                            </div>

                        </div>
                    </div>
                </div>

                {/* ── TABS ── */}
                <div className="sticky top-4 z-40 bg-white rounded-2xl shadow-lg">
                    <div className="flex p-1 gap-1">
                        {(["sessions", "about", "blogs"] as const).map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl font-semibold text-sm transition-all duration-200 ${activeTab === tab
                                    ? "bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-md"
                                    : "text-gray-500 hover:text-gray-700 hover:bg-purple-50"
                                    }`}
                            >
                                {tab === "sessions" && <Brain className="w-4 h-4" />}
                                {tab === "about" && <BookOpen className="w-4 h-4" />}
                                {tab === "blogs" && <Sparkles className="w-4 h-4" />}
                                {tab === "sessions"
                                    ? `Sessions (${instructor.upcomingClasses?.length || 0})`
                                    : tab.charAt(0).toUpperCase() + tab.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>

                {/* ── SESSIONS TAB ── */}
                {activeTab === "sessions" && (
                    <PublicPsychologySessions mentorId={params.id || ""} />
                )}

                {/* ── ABOUT TAB ── */}
                {activeTab === "about" && (
                    <div className="space-y-5">
                        <div className="bg-white/60 backdrop-blur-sm rounded-2xl shadow border border-purple-200 p-5">
                            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                                <GraduationCap className="w-5 h-5 text-violet-600" />
                                Qualifications & Experience
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-3">
                                    {instructor.certification && (
                                        <div className="flex items-center gap-2">
                                            <Award className="w-4 h-4 text-violet-500 flex-shrink-0" />
                                            <button
                                                onClick={() => setShowCertificate(true)}
                                                className="text-sm text-violet-600 hover:text-violet-700 font-semibold flex items-center gap-1 transition-colors hover:underline"
                                            >
                                                View Certification <ArrowRight className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    )}
                                    {instructor.experience && (
                                        <div className="flex items-center gap-2">
                                            <div className="w-2.5 h-2.5 bg-violet-500 rounded-full flex-shrink-0" />
                                            <span className="text-sm text-gray-600">{instructor.experience} of experience</span>
                                        </div>
                                    )}
                                    {instructor.languages && instructor.languages.length > 0 && (
                                        <div className="flex items-center gap-2">
                                            <div className="w-2.5 h-2.5 bg-purple-500 rounded-full flex-shrink-0" />
                                            <span className="text-sm text-gray-600">Languages: {instructor.languages.join(", ")}</span>
                                        </div>
                                    )}
                                </div>
                                <div className="space-y-3">
                                    <div className="flex items-center gap-2">
                                        <div className="w-2.5 h-2.5 bg-indigo-500 rounded-full flex-shrink-0" />
                                        <span className="text-sm text-gray-600">Total Sessions: {instructor.totalClass || 0}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-2.5 h-2.5 bg-blue-500 rounded-full flex-shrink-0" />
                                        <span className="text-sm text-gray-600">Clients Helped: {instructor.totalStudents || 0}</span>
                                    </div>
                                    {instructor.location && (
                                        <div className="flex items-center gap-2">
                                            <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full flex-shrink-0" />
                                            <span className="text-sm text-gray-600">Location: {instructor.location}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="bg-white/60 backdrop-blur-sm rounded-2xl shadow border border-purple-200 p-5">
                            <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                                <Brain className="w-5 h-5 text-violet-600" />
                                About Me
                            </h3>
                            <p className="text-sm text-gray-500 leading-relaxed whitespace-pre-wrap break-words">
                                {instructor.bio ||
                                    "Welcome to my profile! I'm a dedicated psychology specialist passionate about helping individuals achieve mental wellness and emotional balance. Through evidence-based approaches and compassionate care, I support my clients in their journey toward better mental health."}
                            </p>
                        </div>

                        {instructor.Skills && instructor.Skills.length > 0 && (
                            <div className="bg-white/60 backdrop-blur-sm rounded-2xl shadow border border-purple-200 p-5">
                                <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                                    <Target className="w-5 h-5 text-purple-600" />
                                    Areas of Expertise
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    {(showAllSkills ? instructor.Skills : instructor.Skills.slice(0, 6)).map((skill, index) => (
                                        <span
                                            key={index}
                                            className="bg-gradient-to-r from-violet-50 to-purple-50 text-violet-700 px-4 py-2 rounded-full text-sm font-medium border border-violet-200 hover:shadow-sm transition-shadow"
                                        >
                                            {skill}
                                        </span>
                                    ))}
                                    {instructor.Skills.length > 6 && (
                                        <button
                                            onClick={() => setShowAllSkills(!showAllSkills)}
                                            className="bg-gradient-to-r from-violet-600 to-purple-600 text-white px-5 py-2 rounded-full text-sm font-semibold hover:shadow-md transition-all duration-200 hover:from-violet-700 hover:to-purple-700 active:scale-95"
                                        >
                                            {showAllSkills ? "Show Less" : `+ ${instructor.Skills.length - 6} More`}
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* ── BLOGS TAB ── */}
                {activeTab === "blogs" && (
                    <div className="bg-white/60 backdrop-blur-sm rounded-2xl shadow border border-purple-200 p-5">
                        <div className="text-center py-12">
                            <Sparkles className="w-12 h-12 text-violet-300 mx-auto mb-3" />
                            <h3 className="text-lg font-bold text-gray-700 mb-2">Wellness Insights</h3>
                            <p className="text-sm text-gray-400">Blog posts and articles coming soon!</p>
                        </div>
                    </div>
                )}
            </div>

            {/* ── CERTIFICATE MODAL ── */}
            {showCertificate && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
                    <div className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
                        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
                            <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
                                <Award className="w-5 h-5 text-violet-600" />
                                Professional Certification
                            </h3>
                            <button
                                onClick={() => setShowCertificate(false)}
                                className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors text-lg leading-none"
                            >
                                ✕
                            </button>
                        </div>
                        <div className="p-4 overflow-auto max-h-[75vh]">
                            {typeof instructor.certification === "string" ? (
                                (() => {
                                    const certUrl = getAbsoluteImageUrl(instructor.certification);
                                    return instructor.certification.toLowerCase().endsWith(".pdf") ? (
                                        <iframe src={certUrl} className="w-full h-[75vh] rounded-xl" title="Certificate PDF" />
                                    ) : (
                                        <img
                                            src={certUrl}
                                            alt="Professional Certification"
                                            className="w-full rounded-xl object-contain max-h-[75vh]"
                                            onError={(e) => {
                                                const target = e.target as HTMLImageElement;
                                                target.src = "/placeholder-certificate.png";
                                            }}
                                        />
                                    );
                                })()
                            ) : (
                                <div className="flex items-center justify-center h-40">
                                    <p className="text-sm text-gray-400">Certificate not available</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── FLOATING CTA ── */}
            <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3">
                {instructor.socialLinks?.whatsapp && (
                    <a
                        href={`https://wa.me/${instructor.socialLinks.whatsapp}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 bg-emerald-500 rounded-full shadow-xl hover:shadow-emerald-500/30 transition-all hover:scale-110 active:scale-95 flex items-center justify-center text-white"
                        title="Chat on WhatsApp"
                    >
                        <MessageCircle className="w-5 h-5" />
                    </a>
                )}
                <button className="w-12 h-12 bg-gradient-to-r from-violet-600 to-purple-600 rounded-full shadow-xl hover:shadow-violet-500/30 transition-all hover:scale-110 active:scale-95 flex items-center justify-center text-white"
                    title="Book a Session">
                    <Calendar className="w-5 h-5" />
                </button>
            </div>

            <style>{`
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
      `}</style>
        </div>
    );
}